/**
 * Main App component with routing
 */
import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import React from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { AppLayout } from '@/components/Layout/AppLayout';
import { CreateScenarioLayout } from '@/components/Layout/CreateScenarioLayout';
import { HistoryLayout } from '@/components/Layout/HistoryLayout';
import { MainLayout } from '@/components/Layout/MainLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { useAuth } from '@/hooks/useAuth';
import { AuthPage } from '@/pages/AuthPage';
import { CreateScenarioPage } from '@/pages/CreateScenarioPage';
import { EditScenarioPage } from '@/pages/EditScenarioPage';
import { HistoryPage } from '@/pages/HistoryPage';
import { HomePage } from '@/pages/HomePage';
import { MapPage } from '@/pages/MapPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { ScenariosPage } from '@/pages/ScenariosPage';
import { ScenarioComparisonMockPage } from '@/pages/ScenarioComparisonMockPage';
import { ScenarioViewPage } from '@/pages/ScenarioViewPage';
import { TemplatesPage } from '@/pages/TemplatesPage';
import { AdminPanel } from '@/pages/AdminPanel';
import { OrganizationAdminPanel } from '@/pages/OrganizationAdminPanel';
import { InstructionsModal } from '@/components/common/InstructionsModal';
import { PageHelpModal } from '@/components/common/PageHelpModal';
import { useInstructionsGuideStore } from '@/store/instructionsGuideStore';

// Create MUI theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

/** User guide stays open across route changes (not tied to a single layout). */
const GlobalInstructionsHost: React.FC = () => {
  const open = useInstructionsGuideStore((s) => s.open);
  const closeGuide = useInstructionsGuideStore((s) => s.closeGuide);
  return <InstructionsModal open={open} onClose={closeGuide} />;
};

const App: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Auth routes */}
          <Route
            path="/auth"
            element={
              isAuthenticated ? <Navigate to="/app/history" replace /> : <AuthPage />
            }
          />

          {/* Protected routes */}

          {/* Redirect /app route to home */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Navigate to="/app/home" replace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/home"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <HomePage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/map"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <MapPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/history"
            element={
              <ProtectedRoute>
                <HistoryLayout>
                  <HistoryPage />
                </HistoryLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/templates"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TemplatesPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/scenarios"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ScenariosPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/scenario-comparison"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ScenarioComparisonMockPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/scenarios/create"
            element={
              <ProtectedRoute>
                <CreateScenarioLayout>
                  <CreateScenarioPage />
                </CreateScenarioLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/scenarios/:scenarioId/edit"
            element={
              <ProtectedRoute>
                <CreateScenarioLayout>
                  <EditScenarioPage />
                </CreateScenarioLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/scenarios/:scenarioId"
            element={
              <ProtectedRoute>
                <CreateScenarioLayout>
                  <ScenarioViewPage />
                </CreateScenarioLayout>
              </ProtectedRoute>
            }
          />

          {/* Profile page with simple layout */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ProfilePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin panel - must be before catch-all */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPanel />
              </ProtectedRoute>
            }
          />

          {/* Organization Admin panel */}
          <Route
            path="/organization-admin"
            element={
              <ProtectedRoute>
                <OrganizationAdminPanel />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route
            path="/"
            element={
              <Navigate to={isAuthenticated ? "/app/home" : "/auth"} replace />
            }
          />

          {/* Catch all - must be last */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <GlobalInstructionsHost />
        <PageHelpModal />
      </Router>
    </ThemeProvider>
  );
};

export default App;
